import { useRef, useState, type FormEvent } from 'react';
import { CaptchaWidget, type CaptchaWidgetHandle } from '../common/CaptchaWidget';
import { usePlatformCaptchaConfig } from '../../hooks/useCaptchaConfig';

interface MagicLinkLoginFormProps {
  onRequestLink: (email: string, captchaToken?: string) => Promise<void>;
  onBack: () => void;
  error: string | null;
}

type FormState = 'input' | 'sent';

export function MagicLinkLoginForm({ onRequestLink, onBack, error }: MagicLinkLoginFormProps) {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formState, setFormState] = useState<FormState>('input');
  const [formError, setFormError] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<CaptchaWidgetHandle | null>(null);

  const { config: captchaConfig, shouldChallenge } = usePlatformCaptchaConfig();
  const captchaRequired = shouldChallenge('admin_magic_link');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!email) {
      setFormError('Please enter your email address');
      return;
    }

    // Simple email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFormError('Please enter a valid email address');
      return;
    }

    if (captchaRequired && !captchaToken) {
      setFormError('Please complete the CAPTCHA challenge.');
      return;
    }

    setIsSubmitting(true);
    try {
      await onRequestLink(email, captchaToken ?? undefined);
      setFormState('sent');
    } catch {
      // Error is handled by parent
      captchaRef.current?.reset();
      setCaptchaToken(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (formState === 'sent') {
    return (
      <div className="magic-link-sent">
        <div className="magic-link-icon">
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M22 2L11 13" />
            <path d="M22 2L15 22L11 13L2 9L22 2Z" />
          </svg>
        </div>
        <h2>Check your email</h2>
        <p className="auth-subtitle">
          We've sent a magic link to <strong>{email}</strong>
        </p>
        <p className="magic-link-hint">
          Click the link in the email to sign in. The link will expire in 15 minutes.
        </p>
        <button
          type="button"
          className="auth-button auth-button-secondary"
          onClick={() => {
            setFormState('input');
            setEmail('');
          }}
        >
          Try a different email
        </button>
        <button
          type="button"
          className="auth-link"
          onClick={onBack}
          style={{
            marginTop: 'var(--spacing-4)',
            display: 'block',
            width: '100%',
            textAlign: 'center',
          }}
        >
          Back to sign in options
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="auth-form">
      <p className="auth-subtitle" style={{ marginBottom: 'var(--spacing-4)' }}>
        Enter your email address and we'll send you a magic link to sign in.
      </p>

      {(formError || error) && <div className="auth-error">{formError || error}</div>}

      <div className="form-group">
        <label htmlFor="magic-link-email">Email</label>
        <input
          id="magic-link-email"
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="you@example.com"
          autoComplete="email"
          disabled={isSubmitting}
          autoFocus
        />
      </div>

      {captchaRequired && captchaConfig?.provider && captchaConfig.siteKey && (
        <CaptchaWidget
          ref={captchaRef}
          provider={captchaConfig.provider}
          siteKey={captchaConfig.siteKey}
          action="admin_magic_link"
          onVerify={setCaptchaToken}
          onError={() => setCaptchaToken(null)}
          onExpire={() => setCaptchaToken(null)}
        />
      )}

      <button type="submit" className="auth-button" disabled={isSubmitting}>
        {isSubmitting ? 'Sending...' : 'Send Magic Link'}
      </button>

      <button
        type="button"
        className="auth-button auth-button-secondary"
        onClick={onBack}
        disabled={isSubmitting}
      >
        Back to sign in options
      </button>
    </form>
  );
}
